/**
 * Custom hook to handle user information fetching and authentication flow
 * Manages user data fetching, login method validation, and error handling
 */

// React and Next.js core imports
import { useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'

// External libraries and utilities
import { type Address } from 'viem'
import { type User } from '@privy-io/react-auth'
import { openModal } from '@locmod/modal'
import { sendGTMEvent } from '@next/third-parties/google'
import localStorage from '@locmod/local-storage'

// Internal application modules
import { ConnectorName, useWallet } from 'wallet'
import { getSignedRulesStatus, getUserAccountApi } from 'src/utils/api'
import { type UserAccountData } from 'src/utils/helper'
import { getAuthMethod } from 'src/utils'
import { useUserContext } from 'src/contexts/user'
import { useNudge } from 'src/contexts/nudge'
import messages from '@/compositions/modals/ErrorModal/messages'
import { constants } from 'helpers'

interface UtmParams {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
}

interface UseUserInfoFetchProps {
  isAAWallet: boolean
  user: User | null
  loginMethod: string | null
  connector: { name: ConnectorName } | null
  storedInviteCode: string | null
  storedUtmParams: UtmParams
}

export const useUserInfoFetch = ({
  isAAWallet,
  user,
  loginMethod,
  connector,
  storedInviteCode,
  storedUtmParams,
}: UseUserInfoFetchProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const { loginUser, logoutUser, setLoginCompleted, setUserConnecting } =
    useUserContext()
  const { logoutApp } = useWallet()
  const { openNudge, closeNudge } = useNudge()

  /**
   * Fetches and processes user information
   * Handles:
   * - Wallet address validation
   * - User data fetching
   * - Login method verification
   * - Error handling and user feedback
   * - Analytics tracking
   */
  const getUserInfo = useCallback(async () => {
    // Get wallet address based on wallet type
    const walletAddress = isAAWallet
      ? user?.smartWallet?.address
      : user?.wallet?.address

    if (!walletAddress) {
      console.warn('Wallet address is missing.')
      return
    }

    setUserConnecting()

    // Prepare error message with wallet address
    const text = {
      ...messages.loginMethodNotAllowed,
      value: { walletAddress },
    }

    try {
      // Check if user has signed rules
      const status = await getSignedRulesStatus(walletAddress as Address)

      // Prepare request object with UTM params and invite code
      const reqObj = Object.fromEntries(
        Object.entries({
          invite_code: storedInviteCode,
          ...storedUtmParams,
          login_method: !isAAWallet ? 'wallet' : 'social',
        }).filter(([_, value]) => value)
      )

      // Fetch user account data
      const response: UserAccountData = await getUserAccountApi(reqObj)
      if (response) {
        // Validate login method
        const loginMethodCheck =
          response.login_method === 'wallet' &&
          loginMethod &&
          loginMethod !== 'siwe'

        if (loginMethodCheck) {
          logoutApp()
          openModal('ErrorModal', {
            title: messages.loginMethodNotAllowedTitle,
            text,
          })
          throw new Error(text.en)
        }

        // Process successful login
        loginUser(response)

        // Handle unsigned rules
        if (!status.signed) {
          openModal('SignMessageModal')
        }

        // Show nudge for unverified users
        if (!response.is_verified) {
          openNudge({
            message: 'Click here to link your email & unlock higher benefits. ',
          })
        }

        // Close nudge if user is already verified
        if (response.is_verified) {
          closeNudge()
        }

        // Track successful sign-in
        if (status.signed) {
          setLoginCompleted(true)
          sendGTMEvent({
            event: 'sign_in_completed',
            user_id: response.id,
            login_toolkit: 'privy',
            login_method:
              (isAAWallet && loginMethod === 'siwe' ? 'siwe' : loginMethod) ||
              'wallet',
            login_method_id:
              (isAAWallet && getAuthMethod(user as User, loginMethod)) ||
              walletAddress,
            is_auto_login: false,
            is_new_user: response.new_account,
            first_name: response.first_name,
            last_name: response.last_name,
            email_address: response.email_id,
            wallet_app_name:
              connector?.name === ConnectorName.Privy
                ? 'privy'
                : connector?.name,
            wallet_address: response.wallet_address,
          })
        }

        // Mark user info as fetched
        localStorage.setItem(
          constants.localStorageKeys.hasFetchedUserInfo,
          'true'
        )
        router.replace(pathname)

        return response
      }
    } catch (error) {
      // Clear fetch status on error
      localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
      throw error
    }
  }, [
    isAAWallet,
    user,
    loginMethod,
    connector,
    storedInviteCode,
    storedUtmParams,
    setUserConnecting,
    setLoginCompleted,
    loginUser,
    logoutUser,
    router,
    pathname,
    closeNudge,
  ])

  return { getUserInfo }
}
